/* eslint-disable max-lines */
import './calendar-planner.scss';

import {
  AddAbsenceDialog,
  CalendarContext,
  CommonContext,
  HolidayActionKind,
  HolidayBooking,
  IntroDialog,
  isBreakpointMobile,
  NewBookingModal,
  ReducerContext,
  TodayYearButtons,
  Typography,
  useBreakpoint,
} from '@abdoo/common';
import { Alert, Box, Button, Grid } from '@mui/material';
import { addDays, addMonths, differenceInCalendarDays, format, setDate } from 'date-fns';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { CalendarDate } from './CalendarDate/CalendarDate';

interface Year {
  year: number;
  months: Months[];
}

interface Months {
  year: number;
  month: number;
  dates: Date[];
}

interface CalendarPlannerProps {
  isLoading: boolean;
  plannerError: string;
  currentDate: Date;
  addAbsence?: boolean;
}

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function CalendarPlanner({
  isLoading,
  plannerError,
  currentDate,
  addAbsence,
}: CalendarPlannerProps) {
  const { setCurrentPage } = useContext(CommonContext);
  const { holidayState, holidayDispatch, userSettingsState } = useContext(ReducerContext);
  const { refreshHolidayBookings } = useContext(CalendarContext);
  const [calendarPlannerStartDate, setCalendarPlannerStartDate] = useState(
    userSettingsState.start,
  );
  const [calendarPlannerEndDate, setCalendarPlannerEndDate] = useState(
    addDays(addMonths(userSettingsState.start, 24), -1),
  );

  const [dateToScrollTo, setDateToScrollTo] = useState<Date>();

  const calendarRef = useRef<any>();

  const breakpoint = useBreakpoint();
  const isMobile = isBreakpointMobile(breakpoint);

  useEffect(() => {
    setCurrentPage('calendar');
    return () => {
      setCurrentPage(null);
    };
  }, [setCurrentPage]);

  // Given start and end date, return array of dates (Mon - Fri) between them
  useEffect(() => {
    const generateHolidays = (holidayForm: HolidayBooking) => {
      const start = new Date(holidayForm.startDate);
      const end = new Date(holidayForm.endDate);
      return new Array(differenceInCalendarDays(end, start) + 1).fill(start).map(addDays);
    };

    const holidayArray = holidayState.holidayBookings
      .map(generateHolidays)
      .reduce((h1, h2) => h1.concat(h2), []);
    holidayDispatch({
      type: HolidayActionKind.InitialiseHolidays,
      payload: holidayArray,
    });
  }, [holidayState.holidayBookings, holidayDispatch]);

  const onRetry = useCallback(() => {
    refreshHolidayBookings();
  }, [refreshHolidayBookings]);

  const onScroll = useCallback((event: any) => {
    const isAtBottom =
      event.target.scrollHeight - event.srcElement.scrollTop <=
      event.srcElement.offsetHeight + 40;
    const isAtTop = event.target.scrollTop === 0;

    if (isAtBottom) {
      event.target.scrollTop = event.srcElement.scrollTop - 120;
      setCalendarPlannerEndDate((p) => addMonths(p, 12));
    }
    if (isAtTop) {
      event.target.scrollTop = event.srcElement.scrollTop + 120;
      setCalendarPlannerStartDate((p) => addMonths(p, -12));
    }
  }, []);

  const todayId = currentDate.getFullYear() + ' ' + monthNames[currentDate.getMonth()];
  const yearId =
    userSettingsState.start.getFullYear().toString() +
    ' ' +
    monthNames[userSettingsState.start.getMonth()];
  const calendarElement = calendarRef;

  useEffect(() => {
    const current = calendarElement.current;
    current.addEventListener('scroll', onScroll);
    return () => current.removeEventListener('scroll', onScroll);
  }, [calendarElement, onScroll]);

  const daysToShowInCalendar = useMemo(() => {
    const firstDayToShow = setDate(calendarPlannerStartDate, 1);
    const lastDayToShow = setDate(addMonths(calendarPlannerEndDate, 1), 0);

    const daysInYear = differenceInCalendarDays(lastDayToShow, firstDayToShow) + 1;
    const newDays = new Array(daysInYear)
      .fill(firstDayToShow)
      .map((x, i) => addDays(x, i));

    return newDays;
  }, [calendarPlannerStartDate, calendarPlannerEndDate]);

  const yearsToShowInCalendar = useMemo(
    () =>
      daysToShowInCalendar.reduce((years, date) => {
        let year = years.find((y) => y.year === date.getFullYear());

        if (!year) {
          year = { year: date.getFullYear(), months: [] };
          years.push(year);
        }
        const month = year.months.find((m) => m.month === date.getMonth());
        if (month) {
          month.dates.push(date);
        } else {
          year.months.push({
            year: date.getFullYear(),
            month: date.getMonth(),
            dates: [date],
          });
        }

        return years;
      }, [] as Year[]),
    [daysToShowInCalendar],
  );

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const year = hash.substring(1, 5);
      const month = monthNames.find((m) => hash.includes(m));
      const absenceDayId = `${year} ${month}`;
      const elementToScrollTo = document.getElementById(absenceDayId);
      elementToScrollTo?.scrollIntoView();
    }
  }, [isLoading, todayId, addAbsence, calendarPlannerStartDate, hash]);

  useEffect(() => {
    if (!hash && !isLoading && addAbsence) {
      const elementToScrollTo = document.getElementById(todayId);
      elementToScrollTo?.scrollIntoView();
    }
  }, [isLoading, todayId, addAbsence, calendarPlannerStartDate, hash]);

  useEffect(() => {
    if (yearsToShowInCalendar && dateToScrollTo) {
      const monthIdAfterSubmit =
        dateToScrollTo.getFullYear() + ' ' + monthNames[dateToScrollTo.getMonth()];
      location.href = '#';
      location.href = '#' + monthIdAfterSubmit;
    }
  }, [yearsToShowInCalendar, dateToScrollTo]);

  const afterSubmit = useCallback(
    (startDate: Date) => {
      if (startDate) {
        const date = new Date(startDate);
        if (calendarPlannerStartDate > date) {
          setCalendarPlannerStartDate(new Date(date.getFullYear(), date.getMonth(), 1));
        }
        if (calendarPlannerEndDate < date) {
          setCalendarPlannerEndDate(new Date(date.getFullYear(), date.getMonth() + 1, 1));
        }
        setDateToScrollTo(date);
      }
    },
    [calendarPlannerEndDate, calendarPlannerStartDate],
  );

  return (
    <Box id="calendar-planner" ref={calendarRef} overflow="auto">
      {plannerError && plannerError.length > 0 && (
        <>
          <h3>{plannerError}</h3>
          <button onClick={onRetry}>Retry?</button>
        </>
      )}
      <TodayYearButtons
        todayId={todayId}
        yearId={yearId}
        scrollIntoViewOptionsObject={{ behavior: 'smooth' }}
      />
      <Box
        sx={{ padding: '1em 1em 1em 1em' }}
        className={addAbsence && isMobile ? '' : 'calendar-planner-view'}
      >
        {yearsToShowInCalendar &&
          yearsToShowInCalendar.map((year, yearIndex) => (
            <Box
              key={year.year + yearIndex}
              sx={{ paddingBottom: '0.4em', position: 'relative' }}
            >
              <Box
                sx={{
                  paddingTop: '5px',
                  paddingBottom: '2px',
                  backgroundColor: '#fff',
                  position: 'sticky',
                  top: '0px',
                }}
              >
                <Typography
                  sx={{
                    padding: '0.4em',
                    backgroundColor: '#b7b5b5',
                    fontWeight: 'bold',
                    fontSize: '1.05rem',
                  }}
                  isLoading={isLoading}
                  skeletonHeight="4rem"
                >
                  {year.year}
                </Typography>
              </Box>
              <Grid container item sx={{ padding: '0 0.4em' }}>
                {year.months.map((month, monthIndex) => (
                  <Grid
                    container
                    item
                    key={`${month.month}-${monthIndex}`}
                    id={month.year + ' ' + monthNames[month.month]}
                    sx={{ scrollMarginTop: '40px' }}
                  >
                    <Grid xs={12} item>
                      <Typography
                        sx={{
                          marginTop: '0.60em',
                          fontWeight: 'bold',
                          fontSize: '1.05rem',
                        }}
                        isLoading={isLoading}
                        skeletonHeight="2rem"
                        skeletonSx={{
                          width: format(month.dates[0], 'MMMM').length + 'rem',
                        }}
                      >
                        {format(month.dates[0], 'MMMM')}
                      </Typography>
                    </Grid>
                    <Grid item container>
                      {month.dates.map((date, dateIndex) => (
                        <CalendarDate key={dateIndex} date={date} isLoading={isLoading} />
                      ))}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
      </Box>
      <NewBookingModal
        afterSubmit={afterSubmit}
        OnShowManager={IntroDialogWrapper}
        OnShowManagerPrompt={ManagerSetupAlert}
      />
      <AddAbsenceDialog />
    </Box>
  );
}

const IntroDialogWrapper = ({ open, finish }: { open: boolean; finish: () => void }) => {
  return <IntroDialog type="manager" finish={finish} open={open} />;
};

const ManagerSetupAlert = ({
  onOpenManagerSettingsModal,
}: {
  onOpenManagerSettingsModal: () => void;
}) => {
  return (
    <Alert
      severity="info"
      action={
        <Button
          size="small"
          color="primary"
          variant="contained"
          onClick={onOpenManagerSettingsModal}
        >
          Add details
        </Button>
      }
    >
      Add a manager&apos;s details to enable this feature.
    </Alert>
  );
};
